import { useThrottledFn } from 'beautiful-react-hooks'
import { NodeSelection, TextSelection } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useDetectKeyDown } from 'utils/hookUtils'
import styles from '../../Image.module.scss'

export type CaptionProps = {
  getPos: () => number
  view: EditorView
  attrs: Record<string, any>
  containerWidth: number
}

export type CaptionHandles = {
  onCaptionEdit: () => void
}

export const Caption = forwardRef<CaptionHandles, CaptionProps>((props, ref) => {
  const { view, getPos, attrs, containerWidth } = props
  const captionInputRef = useRef<HTMLTextAreaElement>(null)
  const [caption, setCaption] = useState<string | null>(attrs.description === '' ? null : attrs.description)
  const [captionInputHeight, setCaptionInputHeight] = useState<number>(0)

  useImperativeHandle(ref, () => ({
    onCaptionEdit() {
      if (caption == null) {
        setCaption('')
      }
      setTimeout(() => captionInputRef.current?.focus())
    },
  }))

  useEffect(() => {
    if (attrs.description === '' && document.activeElement !== captionInputRef.current) {
      setCaption(null)
    } else if (attrs.description !== caption) {
      setCaption(attrs.description)
    }
  }, [attrs.description]) // eslint-disable-line react-hooks/exhaustive-deps

  // Move out of the textarea with the keyboard.
  useDetectKeyDown(['ArrowUp', 'ArrowDown', 'Escape'], (e) => {
    if (caption == null || !captionInputRef.current?.matches(':focus')) {
      return
    }
    if (
      (e.key === 'ArrowUp' && captionInputRef.current.selectionStart > 0) ||
      (e.key === 'ArrowDown' && captionInputRef.current.selectionStart < captionInputRef.current.textLength)
    ) {
      return
    }
    const direction = e.key === 'ArrowUp' ? -1 : 1
    const $pos = view.state.doc.resolve(getPos())
    const sel = TextSelection.findFrom($pos, direction, true) || NodeSelection.near($pos)
    view.dispatch(view.state.tr.setSelection(sel))
    view.focus()
    e.preventDefault()
    e.stopPropagation()
  })

  const applyCaptionChangeToAttrs = useThrottledFn(
    (newCaption: string) => {
      const {
        state: { tr },
        dispatch,
      } = view
      tr.setNodeMarkup(getPos(), undefined, { ...attrs, description: newCaption })
      dispatch(tr)
    },
    200,
    undefined,
    [attrs],
  )

  const onCaptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCaption(e.target.value)
    applyCaptionChangeToAttrs(e.target.value)
  }

  const onBlur = () => {
    if (caption === '') {
      setCaption(null)
    }
  }

  const sizeDetectionHelperRef = (node: HTMLDivElement | null) => {
    if (node && captionInputHeight !== node.clientHeight) {
      setCaptionInputHeight(node.clientHeight)
    }
  }

  if (caption == null) {
    return null
  }

  return (
    <div className={styles.caption} style={{ width: `${containerWidth}px` }}>
      <textarea
        ref={captionInputRef}
        value={caption === 'Caption here' ? '' : caption}
        placeholder="Caption here"
        style={{ height: captionInputHeight ? `${captionInputHeight}px` : 'auto' }}
        onChange={onCaptionChange}
        onBlur={onBlur}
      />
      <div ref={sizeDetectionHelperRef} className={styles.sizeDetectionHelper}>
        {caption + '\n'}
      </div>
    </div>
  )
})
